import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiHeadphones,
  FiLayers,
  FiPhoneCall,
  FiZap,
} from "react-icons/fi";
import { useLocalContext } from "../../context/LocalContext";
import "./Hero.css";

const Hero = ({ onSubmitLead }) => {
  const reduceMotion = useReducedMotion();
  const { webinfo = {} } = useLocalContext();

  const brandName = webinfo?.name || "digivirtuehost.com";
  const phoneDisplay = webinfo?.phone || webinfo?.phonecall || "+1 (000) 000-0000";

  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: "",
  });

  const [status, setStatus] = useState({
    loading: false,
    ok: "",
    err: "",
  });

  const services = [
    "Shared / WordPress hosting",
    "VPS & cloud servers",
    "Domains & DNS",
    "SSL, email & backups",
    "Migration from another host",
    "Not sure — advise me",
  ];

  const trustPoints = [
    "SSD storage & modern PHP / HTTP/2 ready stacks",
    "Free SSL, daily backups, and 24/7 monitoring options",
    "Human support for setup, DNS, and migrations",
  ];

  const metrics = [
    { value: "99.9%", label: "Uptime target", icon: <FiLayers /> },
    { value: "24/7", label: "Expert support", icon: <FiHeadphones /> },
    { value: "Fast", label: "Global edge-ready", icon: <FiZap /> },
  ];

  const onChange = (e) => {
    setStatus({ loading: false, ok: "", err: "" });
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.phone.trim()) {
      setStatus({
        loading: false,
        ok: "",
        err: "Please enter your name and phone number.",
      });
      return;
    }

    try {
      setStatus({ loading: true, ok: "", err: "" });

      await onSubmitLead?.({
        ...form,
        source: "hosting_hero_lead",
      });

      setStatus({
        loading: false,
        ok: "Thanks! Our team will reach out shortly.",
        err: "",
      });

      setForm({
        name: "",
        phone: "",
        service: "",
      });
    } catch (error) {
      setStatus({
        loading: false,
        ok: "",
        err: "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <section className="heroPremium">
      <div className="heroPremium-blur heroPremium-blurOne" />
      <div className="heroPremium-blur heroPremium-blurTwo" />

      <div className="heroPremium-wrap">
        <motion.div
          className="heroPremium-left"
          initial={reduceMotion ? false : { opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="heroPremium-badge">
            <span className="heroPremium-badgeDot" />
            Reliable web hosting & cloud
          </div>

          <h1 className="heroPremium-title">
            Hosting that keeps your site
            <span> fast, secure, and online</span>
          </h1>

          <p className="heroPremium-subtitle">
            {brandName} delivers dependable shared, WordPress, and VPS hosting
            with clear pricing, strong security, and support when you need it —
            from DNS to SSL to migrations.
          </p>

          <div className="heroPremium-points">
            {trustPoints.map((item) => (
              <div className="heroPremium-point" key={item}>
                <FiCheckCircle />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="heroPremium-actions">
            <Link to="/services" className="heroPremium-btn heroPremium-btnPrimary">
              View hosting plans
              <FiArrowRight />
            </Link>

            <a href={`tel:${phoneDisplay}`} className="heroPremium-btn heroPremium-btnGhost">
              <FiPhoneCall />
              Call Now
            </a>
          </div>

          <div className="heroPremium-stats">
            {metrics.map((item) => (
              <div className="heroPremium-statCard" key={item.label}>
                <div className="heroPremium-statIcon">{item.icon}</div>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="heroPremium-right"
          initial={reduceMotion ? false : { opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reduceMotion ? 0 : 0.5,
            delay: reduceMotion ? 0 : 0.1,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <div className="heroPremium-formCard">
            <div className="heroPremium-formTop">
              <p className="heroPremium-formEyebrow">Talk to hosting</p>
              <h2>Get a tailored plan</h2>
              <p>
                Share your domain, traffic, and stack — we’ll recommend the right
                hosting tier and help with migration if you’re moving from
                another provider.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="heroPremium-form">
              <div className="heroPremium-field">
                <label htmlFor="name">Your Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="heroPremium-field">
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="heroPremium-field">
                <label htmlFor="service">What are you looking for?</label>
                <select
                  id="service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                >
                  <option value="">Select an option</option>
                  {services.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              {status.err ? <div className="heroPremium-msg heroPremium-msgErr">{status.err}</div> : null}
              {status.ok ? <div className="heroPremium-msg heroPremium-msgOk">{status.ok}</div> : null}

              <button
                type="submit"
                className="heroPremium-submit"
                disabled={status.loading}
              >
                {status.loading ? "Submitting..." : "Request hosting quote"}
              </button>
            </form>

            <div className="heroPremium-miniTrust">
              <span>No obligation</span>
              <span>Migration help</span>
              <span>Same-day reply</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
